import { FieldTemplateProps, utils } from "@rjsf/core"
import { Button, Title } from "dash-styled"
import React, { useCallback, useContext } from "react"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import {
    ToolsContext,
    StylingContext,
    BaseCustomField,
    BaseInvertedCustomField
} from "resha"

function LabelInput({
    label,
    onChange
}: {
    onChange: (value: string) => void
    label: string
}) {
    return (
        <input
            type="text"
            onBlur={event => onChange(event.target.value)}
            defaultValue={label}
        />
    )
}

function WrapIfAdditional(props: FieldTemplateProps) {
    const {
        classNames,
        disabled,
        label,
        onKeyChange,
        onDropPropertyClick,
        readonly,
        required,
        schema
    } = props
    const keyLabel = `${label} Key` // i18n ?
    const additional = schema.hasOwnProperty(utils.ADDITIONAL_PROPERTY_FLAG)

    if (!additional) {
        return <div className={classNames}>{props.children}</div>
    }
    return (
        <div className={classNames}>
            <div className="flex gap-2 items-start justify-between">
                <div>
                    <Title
                        component="h4"
                        variant="heading4"
                        className="mb-1 capitalize"
                    >
                        {keyLabel}
                        {!readonly && !disabled && required && <span>*</span>}
                    </Title>
                    <LabelInput label={label} onChange={onKeyChange} />
                </div>
                <div className="flex-grow flex-shrink">{props.children}</div>
                <Button
                    size="xs"
                    color="outline-pink"
                    variant="contained2"
                    disabled={disabled || readonly}
                    onClick={onDropPropertyClick(label)}
                >
                    <TrashIcon />
                </Button>
            </div>
        </div>
    )
}

// https://github.com/rjsf-team/react-jsonschema-form/blob/master/packages/core/src/components/fields/SchemaField.js
// Copy of FieldTemplate with hidden label and errors
export function FieldTemplate<T>(props: FieldTemplateProps<T>) {
    const {
        children,
        hidden,
        required,
        displayLabel,
        readonly,
        disabled,
        formData,
        rawHelp,
        schema,
        uiSchema,
        rawDescription,
        onChange
    } = props
    const { skipMissingDataField } = useContext(ToolsContext)
    const { invertLayout } = useContext(StylingContext)
    const handleSelectSuggestion = useCallback(
        (selected: T | undefined) => {
            if (selected !== undefined) {
                onChange(selected)
            }
        },
        [onChange]
    )

    if (skipMissingDataField && formData === undefined) {
        return null
    }

    if (hidden) {
        return <div className="hidden">{children}</div>
    }

    const BaseField = invertLayout ? BaseInvertedCustomField : BaseCustomField
    return (
        <WrapIfAdditional {...props}>
            {displayLabel && props.schema.type !== "null" ? (
                <BaseField
                    rawDescription={rawDescription}
                    rawHelp={rawHelp}
                    showRequired={required && !readonly && !disabled}
                    schema={schema}
                    uiSchema={uiSchema}
                    onSelectSuggestion={handleSelectSuggestion}
                >
                    {children}
                </BaseField>
            ) : (
                children
            )}
        </WrapIfAdditional>
    )
}
